'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { urlForImage } from '@/sanity/lib/utils'
import type { Image as SanityImage } from 'sanity'

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-white"></div>
    </div>
  )
}

interface LightboxProps {
  isOpen: boolean
  onClose: () => void
  imageSrc: string
  alt: string
}

export function Lightbox({ isOpen, onClose, imageSrc, alt }: LightboxProps) {
  const [isImageLoading, setIsImageLoading] = useState(true)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}>
      <div className="relative max-h-[100vh] max-w-[90vw]">
        {/* Close button - only show when image is loaded */}
        {!isImageLoading && (
          <button
            className="absolute -top-10 right-0 z-10 text-2xl font-bold text-white transition-opacity duration-300 hover:text-gray-300"
            onClick={onClose}>
            ×
          </button>
        )}

        {/* Loading spinner */}
        {isImageLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        <div className="relative">
          <Image
            key={imageSrc} // Reset component when image changes
            src={imageSrc}
            alt={alt}
            width={1600}
            height={1200}
            className={`max-h-[90vh] object-contain transition-opacity duration-300 ${
              isImageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
            onLoadStart={() => setIsImageLoading(true)}
            onClick={e => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  )
}

interface ClickableImageProps {
  image: SanityImage
  alt: string
  classesWrapper?: string
  children: React.ReactNode
}

export function ClickableImage({ image, alt, classesWrapper, children }: ClickableImageProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  const imageUrl = image ? urlForImage(image)?.url() || '' : ''

  return (
    <>
      <div
        className={`${classesWrapper} cursor-pointer transition-transform hover:scale-[1.02]`}
        onClick={() => setIsLightboxOpen(true)}>
        {children}
      </div>
      <Lightbox
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        imageSrc={imageUrl}
        alt={alt}
      />
    </>
  )
}
